import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "@/api/endpoints";
import { logoutUser } from "@/features/auth/authSlice";

const baseStep2Data = {
  company_name: "",
  poc_name: "",
  poc_designation: "",
  city_id: "",
  city_name: "",
  email: "",
  registered_address: "",
  company_logo: "",
  company_logo_preview: "",
  coverage_state_ids: [],
  region_ids: [],
};

const baseStep3Data = {
  industry_ids: [],
  website: "",
  gstin: "",
  gst_number: "",
  investment_range: "",
  short_description: "",
  year_founded: "",
  social_link: "",
  pan_number: "",
  income_tax_number: "",
  long_description: "",
  moq: "",
  pricing_structure: "",
  return_policy: "",
  warranty_terms: "",
  distribution_requirements: "",
  desired_coverage: "",
  credit_terms_offered: "",
  warehouse_capacity: "",
  team_size: "",
  field_sales_staff: "",
  accepted_credit_terms: "",
  desired_outcomes: "",
  accepted_payment_terms: "",
  coverage_country_ids: [],
};

const createInitialStep2Data = () => ({
  ...baseStep2Data,
  product_category_ids: [],
});

const createInitialStep3Data = () => ({
  ...baseStep3Data,
});

// Removed localStorage onboarding tracking - now using database onboarding_step
const createFreshOnboardingState = () => ({
  metadata: {
    lastHydratedCompanyId: null,
  },
  step2: createInitialStep2Data(),
  step3: createInitialStep3Data(),
});

const createInitialOnboardingState = () => createFreshOnboardingState();

const getStepInitialData = (step) =>
  step === "step2" ? createInitialStep2Data() : createInitialStep3Data();

const attemptParseJsonArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toNumberArray = (value) =>
  attemptParseJsonArray(value)
    .map((entry) => Number(entry))
    .filter((entry) => !Number.isNaN(entry));

const normalizeNumberToString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : String(parsed);
};

const extractIndustryIds = (company = {}) => {
  const ids = new Set();
  const pushId = (value) => {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      ids.add(parsed);
    }
  };

  toNumberArray(company.industry_ids).forEach(pushId);

  if (Array.isArray(company.companyIndustries)) {
    company.companyIndustries.forEach((entry) => {
      if (entry?.industry_id !== undefined) {
        pushId(entry.industry_id);
      } else if (entry?.industry?.id !== undefined) {
        pushId(entry.industry.id);
      }
    });
  }

  if (company.industry_id !== undefined) {
    pushId(company.industry_id);
  }

  return Array.from(ids);
};

const extractSocialLink = (company) => {
  if (company?.social_link) return company.social_link;
  if (typeof company?.social_links === "string") return company.social_links;
  if (company?.social_links && typeof company.social_links === "object") {
    const firstKey = Object.keys(company.social_links)[0];
    if (firstKey) return company.social_links[firstKey];
  }
  return "";
};

const mapCompanyToStep2 = (company = {}) => {
  // Derive category IDs from scalar field and relation
  let categoryIds = toNumberArray(company.category_ids);
  if ((!categoryIds || categoryIds.length === 0) && Array.isArray(company.companyCategories)) {
    const fromRelation = company.companyCategories
      .map((entry) => {
        if (entry?.category_id != null) return Number(entry.category_id);
        if (entry?.category?.id != null) return Number(entry.category.id);
        return null;
      })
      .filter((id) => id !== null && !Number.isNaN(id));
    if (fromRelation.length > 0) {
      categoryIds = Array.from(new Set(fromRelation));
    }
  }

  // Derive region IDs from scalar field and relation
  let regionIds = toNumberArray(company.region_ids);
  if ((!regionIds || regionIds.length === 0) && Array.isArray(company.companyRegions)) {
    const fromRelation = company.companyRegions
      .map((entry) => {
        if (entry?.region_id != null) return Number(entry.region_id);
        if (entry?.region?.id != null) return Number(entry.region.id);
        return null;
      })
      .filter((id) => id !== null && !Number.isNaN(id));
    if (fromRelation.length > 0) {
      regionIds = Array.from(new Set(fromRelation));
    }
  }

  // Derive city from scalar field and relation
  const derivedCityId =
    company.city_id ?? company.companyCity?.city_id ?? company.companyCity?.city?.id ?? "";
  const derivedCityName =
    company.city || company.companyCity?.city?.name || company.city_name || "";

  return {
    company_name: company.company_name || "",
    poc_name: company.poc_name || company.contact_person || "",
    poc_designation: company.poc_designation || "",
    city_id: derivedCityId,
    city_name: derivedCityName,
    email: company.email || company.poc_email || "",
    registered_address:
      company.registered_address ||
      company.address_1 ||
      company.address_2 ||
      company.address_3 ||
      "",
    company_logo: company.company_logo || null,
    company_logo_preview: company.company_logo || null,
    product_category_ids: categoryIds,
    region_ids: regionIds,
    coverage_state_ids: toNumberArray(
      company.coverage_state_ids ?? company.coverage_states ?? company.target_state_ids
    ),
  };
};

const mapCompanyToStep3 = (company = {}) => ({
  industry_ids: extractIndustryIds(company),
  website: company.website || "",
  gstin: company.gstin || company.gst_number || "",
  investment_range: company.investment_range || "",
  short_description: company.short_description || "",
  year_founded: normalizeNumberToString(
    company.founded_year ?? company.year_of_establishment ?? company.year_founded
  ),
  social_link: extractSocialLink(company),
  pan_number: company.pan_number || company.income_tax_number || "",
  long_description: company.long_description || "",
  moq: normalizeNumberToString(company.moq),
  pricing_structure: company.pricing_structure || "",
  return_policy: company.return_policy || "",
  warranty_terms: company.warranty_terms || "",
  distribution_requirements: company.distribution_requirements || "",
  desired_coverage: company.desired_coverage || "",
  credit_terms_offered: company.credit_terms_offered || "",
  warehouse_capacity: company.warehouse_capacity || "",
  team_size: normalizeNumberToString(company.team_size),
  field_sales_staff: normalizeNumberToString(company.field_sales_staff),
  accepted_credit_terms: company.accepted_credit_terms || "",
  desired_outcomes: company.desired_outcomes || "",
  accepted_payment_terms: company.accepted_payment_terms || "",
  coverage_country_ids: toNumberArray(
    company.coverage_country_ids ?? company.coverage_countries ?? company.target_country_ids
  ),
});

const extractCompanyPayloadData = (payload) => {
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

const parseOwnerArgs = (args) => {
  if (args && typeof args === "object" && !Array.isArray(args)) {
    const { owner_user_id, force = false, forceRefresh = false } = args;
    return { owner_user_id, forceRefresh: force || forceRefresh };
  }
  return { owner_user_id: args, forceRefresh: false };
};

let listMyCompanyInFlight = false;

export const listMyCompany = createAsyncThunk(
  "companies/listMyCompany",
  async (args, { rejectWithValue }) => {
    const { owner_user_id } = parseOwnerArgs(args);
    try {
      if (!owner_user_id) {
        return rejectWithValue("owner_user_id is required");
      }
      const res = await Api.companies.list({ page: 1, pageSize: 1, owner_user_id });
      return res.data || [];
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    } finally {
      listMyCompanyInFlight = false;
    }
  },
  {
    condition: (args, { getState }) => {
      const { owner_user_id, forceRefresh } = parseOwnerArgs(args);
      if (!owner_user_id) return false;

      if (listMyCompanyInFlight) return false;

      if (forceRefresh) {
        listMyCompanyInFlight = true;
        return true;
      }

      const { companies } = getState();
      const { myCompanyMeta = {} } = companies || {};

      if (myCompanyMeta.isFetching) return false;

      if (
        myCompanyMeta.lastOwnerId === owner_user_id &&
        myCompanyMeta.lastFetchedAt &&
        Date.now() - myCompanyMeta.lastFetchedAt < 60 * 1000
      ) {
        return false;
      }

      listMyCompanyInFlight = true;
      return true;
    },
  }
);

// List all companies (paginated)
export const listCompanies = createAsyncThunk(
  "companies/list",
  async (params = { page: 1, pageSize: 20 }, { rejectWithValue }) => {
    try {
      const res = await Api.companies.list(params);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Fetch all companies (with pagination and filtering)
export const fetchAllCompanies = createAsyncThunk(
  "companies/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await Api.companies.fetchAll(params);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Get a company by id
export const getCompany = createAsyncThunk("companies/get", async (id, { rejectWithValue }) => {
  try {
    const res = await Api.companies.get(id);
    return res.data?.data || res.data;
  } catch (e) {
    return rejectWithValue(e.response?.data || e.message);
  }
});

export const getCompanyBySlug = createAsyncThunk(
  "companies/getBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const res = await Api.companies.getBySlug(slug);
      return res.data?.data || res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Create a company
export const createCompany = createAsyncThunk(
  "companies/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Api.companies.create(payload);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Update a company
export const updateCompany = createAsyncThunk(
  "companies/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Api.companies.update(id, payload);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Delete a company
export const deleteCompany = createAsyncThunk(
  "companies/delete",
  async (id, { rejectWithValue }) => {
    try {
      await Api.companies.remove(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const setupCompanyProfile = createAsyncThunk(
  "companies/setupCompanyProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const processedPayload = { ...payload };

      if (Array.isArray(processedPayload.factory_locations)) {
        processedPayload.factory_locations = processedPayload.factory_locations.filter(
          (loc) => loc && (loc.address || loc.city || loc.state || loc.pincode)
        );
        if (processedPayload.factory_locations.length === 0) {
          delete processedPayload.factory_locations;
        }
      }

      if (Array.isArray(processedPayload.territories_served)) {
        processedPayload.territories_served = processedPayload.territories_served.filter(
          (terr) => terr && (terr.city || terr.state || terr.pincode)
        );
        if (processedPayload.territories_served.length === 0) {
          delete processedPayload.territories_served;
        }
      }

      let productCatalogForUpload = null;
      const hadCatalogInput = Array.isArray(processedPayload.product_catalog);
      if (hadCatalogInput) {
        productCatalogForUpload = [];

        processedPayload.product_catalog.forEach((item) => {
          if (!item) return;
          // eslint-disable-next-line no-unused-vars
          const { image_file, image_preview: _image_preview, ...rest } = item;
          const cleanedItem = { ...rest };

          if (!cleanedItem.name && !cleanedItem.image && !cleanedItem.specs && !cleanedItem.price) {
            return;
          }

          productCatalogForUpload.push(cleanedItem);

          if (image_file instanceof File) {
            if (!processedPayload.__productImages) {
              processedPayload.__productImages = [];
            }
            processedPayload.__productImages.push(image_file);
          }
        });

        if (productCatalogForUpload.length === 0) {
          productCatalogForUpload = null;
        }
      }

      const formData = new FormData();

      let resolvedCompanyLogo = null;
      if (processedPayload.company_logo instanceof File) {
        resolvedCompanyLogo = processedPayload.company_logo;
      } else if (
        typeof processedPayload.company_logo === "string" &&
        processedPayload.company_logo.trim().length > 0
      ) {
        resolvedCompanyLogo = processedPayload.company_logo;
      } else if (
        typeof processedPayload.company_logo_preview === "string" &&
        processedPayload.company_logo_preview.trim().length > 0 &&
        !processedPayload.company_logo_preview.startsWith("data:")
      ) {
        resolvedCompanyLogo = processedPayload.company_logo_preview;
      }

      if (resolvedCompanyLogo) {
        formData.append("company_logo", resolvedCompanyLogo);
      }
      delete processedPayload.company_logo;

      if (productCatalogForUpload) {
        formData.append("product_catalog", JSON.stringify(productCatalogForUpload));
      } else if (hadCatalogInput) {
        formData.append("product_catalog", JSON.stringify([]));
      }
      delete processedPayload.product_catalog;

      if (processedPayload.__productImages) {
        processedPayload.__productImages.forEach((file) => {
          formData.append("product_images", file);
        });
        delete processedPayload.__productImages;
      }

      Object.entries(processedPayload).forEach(([key, value]) => {
        if (value === undefined) return;

        if (value instanceof File) {
          formData.append(key, value);
          return;
        }

        // Handle null explicitly to clear fields on backend
        if (value === null) {
          formData.append(key, "");
          return;
        }

        if (Array.isArray(value) || typeof value === "object") {
          formData.append(key, JSON.stringify(value));
          return;
        }

        formData.append(key, value);
      });

      const res = await Api.companies.profileSetup(formData);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const listCompanyDocuments = createAsyncThunk(
  "companies/listCompanyDocuments",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await Api.companies.listDocuments(companyId);
      return res.data || [];
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const uploadCompanyDocument = createAsyncThunk(
  "companies/uploadCompanyDocument",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Api.companies.createDocument(payload);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Save for later functionality
export const saveForLater = createAsyncThunk(
  "companies/saveForLater",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await Api.companies.saveForLater(companyId);
      return res.data || { companyId, saved: true };
    } catch (e) {
      // Extract error message from different possible response structures
      const errorData = e.response?.data || e.response || e;
      const errorMessage =
        errorData?.message || errorData?.error?.message || e.message || "Failed to save company";
      const errorCode = errorData?.code || errorData?.error?.code;
      const statusCode = errorData?.statusCode || e.response?.status;

      return rejectWithValue({
        message: errorMessage,
        code: errorCode,
        statusCode: statusCode,
        ...errorData,
      });
    }
  }
);

export const removeFromSaved = createAsyncThunk(
  "companies/removeFromSaved",
  async (companyId, { rejectWithValue }) => {
    try {
      await Api.companies.removeFromSaved(companyId);
      return companyId;
    } catch (e) {
      // Extract error message from different possible response structures
      const errorData = e.response?.data || e.response || e;
      const errorMessage =
        errorData?.message || errorData?.error?.message || e.message || "Failed to remove company";
      const errorCode = errorData?.code || errorData?.error?.code;
      const statusCode = errorData?.statusCode || e.response?.status;

      return rejectWithValue({
        message: errorMessage,
        code: errorCode,
        statusCode: statusCode,
        ...errorData,
      });
    }
  }
);

export const getSavedForLater = createAsyncThunk(
  "companies/getSavedForLater",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await Api.companies.getSavedForLater(userId);
      return res.data || res || [];
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// Prevent duplicate inflight requests for getDMSCompany
let getDMSCompanyInFlight = false;

// Get DMS company with hierarchy (parent + subsidiaries)
export const getDMSCompany = createAsyncThunk(
  "companies/getDMSCompany",
  async (_, { rejectWithValue }) => {
    try {
      const res = await Api.companies.getDMSCompany();
      return res.data || res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    } finally {
      // reset in-flight flag so future requests can proceed
      getDMSCompanyInFlight = false;
    }
  },
  {
    condition: (_, { getState }) => {
      if (getDMSCompanyInFlight) return false;
      const { companies } = getState();
      if (companies?.isLoadingDMSCompany) return false;
      getDMSCompanyInFlight = true;
      return true;
    },
  }
);

// Match score/compatibility functionality
export const getMatchScore = createAsyncThunk(
  "companies/getMatchScore",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await Api.compatibility.getScores(companyId);
      return res.data || res || [];
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const calculateMatchScore = createAsyncThunk(
  "companies/calculateMatchScore",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await Api.compatibility.calculate(companyId);
      return res.data || res || [];
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

const initialState = {
  // CRUD collections
  items: [],
  pagination: { total: 0, page: 1, pageSize: 20 },
  current: null,

  // Existing single-company (myCompany) convenience
  myCompany: null,

  // DMS company with hierarchy (parent + subsidiaries)
  dmsCompany: null,
  isLoadingDMSCompany: false,
  dmsCompanyError: null,

  // Documents
  documents: [],

  // Save for later
  savedCompanies: [],
  savedCompanyIds: [], // Quick lookup array
  isSaving: false,
  isRemoving: false,
  isLoadingSaved: false,
  saveError: null,

  // Match scores/compatibility
  matchScores: [], // Array of compatibility scores
  isCalculatingMatch: false,
  isLoadingMatch: false,
  matchError: null,

  // Flags
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  isCreating: false,
  isUpdating: false,
  createError: null,
  updateError: null,
  deleteError: null,

  // Filters
  filters: {
    search: "",
    role: undefined,
  },

  // Errors
  error: null,

  onboarding: createInitialOnboardingState(),
  myCompanyMeta: {
    isFetching: false,
    lastOwnerId: null,
    lastFetchedAt: null,
    lastRequestedOwnerId: null,
  },
};

const companySlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { search: "", role: undefined };
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
    clearMatchError: (state) => {
      state.matchError = null;
    },
    // Helper to check if a company is saved (optimistic update)
    setCompanySavedStatus: (state, action) => {
      const { companyId, isSaved } = action.payload;
      if (isSaved && !state.savedCompanyIds.includes(companyId)) {
        state.savedCompanyIds.push(companyId);
      } else if (!isSaved) {
        state.savedCompanyIds = state.savedCompanyIds.filter((id) => id !== companyId);
      }
    },
    updateOnboardingStep: (state, action) => {
      const { step, data } = action.payload || {};
      if (!step || !data) return;
      if (!state.onboarding[step]) state.onboarding[step] = getStepInitialData(step);
      state.onboarding[step] = { ...state.onboarding[step], ...data };
    },
    setOnboardingStep: (state, action) => {
      const { step, data } = action.payload || {};
      if (!step || !data) return;
      state.onboarding[step] = { ...getStepInitialData(step), ...data };
    },
    resetOnboardingStep: (state, action) => {
      const { step } = action.payload || {};
      if (!step) return;
      state.onboarding[step] = getStepInitialData(step);
    },
    resetOnboarding: (state) => {
      state.onboarding = createFreshOnboardingState();
    },
    hydrateOnboardingFromCompany: (state, action) => {
      const company = action.payload;
      if (!company) return;
      state.onboarding.step2 = { ...state.onboarding.step2, ...mapCompanyToStep2(company) };
      state.onboarding.step3 = { ...state.onboarding.step3, ...mapCompanyToStep3(company) };
      state.onboarding.metadata.lastHydratedCompanyId = company.id ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(listCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.data || [];
        state.pagination = {
          total: action.payload.meta?.total ?? 0,
          page: action.payload.meta?.page ?? 1,
          pageSize: action.payload.meta?.pageSize ?? 20,
        };
      })
      .addCase(listCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllCompanies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.data?.items || [];
        state.pagination = {
          total: action.payload.data?.total ?? 0,
          page: action.payload.data?.page ?? 1,
          pageSize: action.payload.data?.pageSize ?? 20,
        };
      })
      .addCase(fetchAllCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get
      .addCase(getCompany.fulfilled, (state, action) => {
        state.current = action.payload || null;
      })
      .addCase(getCompanyBySlug.fulfilled, (state, action) => {
        state.current = action.payload || null;
      })

      // Create
      .addCase(createCompany.pending, (state) => {
        state.isSubmitting = true;
        state.isCreating = true;
        state.createError = null;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isCreating = false;
        const created = action.payload?.data || action.payload;
        if (created) {
          state.items.unshift(created);
          state.pagination.total += 1;
        }
        state.createError = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isSubmitting = false;
        state.isCreating = false;
        state.createError = action.payload;
        state.error = action.payload;
      })

      // Update
      .addCase(updateCompany.pending, (state) => {
        state.isSubmitting = true;
        state.isUpdating = true;
        state.updateError = null;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isUpdating = false;
        const updated = action.payload?.data || action.payload;

        const mergeCompanyData = (existing, updated) => {
          if (!existing || !updated) return updated || existing;

          const merged = { ...existing };

          const sensitiveFields = [
            "gst_number",
            "gstin",
            "pan_number",
            "income_tax_number",
            "social_link",
            "social_links",
            "support_offered_ids",
            "coverage_state_ids",
            "region_ids",
            "category_ids",
            "industry_ids",
            "desired_distributor_type",
            "desired_distributor_type_ids",
            "investment_range",
            "credit_terms_value",
            "credit_terms_unit",
            "total_current_distributors",
            "total_current_manufacturers",
          ];

          Object.keys(updated).forEach((key) => {
            if (sensitiveFields.includes(key)) {
              if (Array.isArray(updated[key])) {
                if (updated[key].length > 0) {
                  merged[key] = updated[key];
                } else if (Array.isArray(existing[key]) && existing[key].length > 0) {
                  merged[key] = existing[key];
                } else {
                  merged[key] = updated[key];
                }
              } else if (
                updated[key] !== null &&
                updated[key] !== undefined &&
                updated[key] !== ""
              ) {
                merged[key] = updated[key];
              } else if (
                existing[key] !== null &&
                existing[key] !== undefined &&
                existing[key] !== ""
              ) {
                merged[key] = existing[key];
              } else {
                merged[key] = updated[key];
              }
            } else {
              merged[key] = updated[key];
            }
          });

          return merged;
        };

        const i = state.items.findIndex((x) => x.id === updated?.id);
        if (i !== -1) {
          state.items[i] = mergeCompanyData(state.items[i], updated);
        }
        if (state.current?.id === updated?.id) {
          state.current = mergeCompanyData(state.current, updated);
        }
        if (state.myCompany?.id === updated?.id) {
          state.myCompany = mergeCompanyData(state.myCompany, updated);
        }
        state.updateError = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isSubmitting = false;
        state.isUpdating = false;
        state.updateError = action.payload;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCompany.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isDeleting = false;
        const id = action.payload;
        state.items = state.items.filter((x) => x.id !== id);
        if (state.current?.id === id) state.current = null;
        if (state.myCompany?.id === id) state.myCompany = null;
        state.pagination.total = Math.max(0, (state.pagination.total || 0) - 1);
        state.deleteError = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload;
        state.error = action.payload;
      })

      .addCase(listMyCompany.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.myCompanyMeta.isFetching = true;
        state.myCompanyMeta.lastRequestedOwnerId =
          parseOwnerArgs(action.meta.arg).owner_user_id || null;
      })
      .addCase(listMyCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        const items = action.payload;
        const hasCompany = Array.isArray(items) && items.length > 0;
        state.myCompany = hasCompany ? items[0] : null;

        if (!hasCompany && state.onboarding?.metadata?.lastHydratedCompanyId) {
          state.onboarding = createFreshOnboardingState();
        }
        state.myCompanyMeta.isFetching = false;
        state.myCompanyMeta.lastOwnerId =
          parseOwnerArgs(action.meta.arg).owner_user_id || state.myCompanyMeta.lastOwnerId;
        state.myCompanyMeta.lastFetchedAt = Date.now();
      })
      .addCase(listMyCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.myCompanyMeta.isFetching = false;
      })
      .addCase(setupCompanyProfile.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(setupCompanyProfile.fulfilled, (state, action) => {
        state.isSubmitting = false;
        const companyData = extractCompanyPayloadData(action.payload);
        state.myCompany = companyData ?? action.payload;

        if (companyData?.id) {
          state.onboarding.metadata.lastHydratedCompanyId = companyData.id;

          const existingIndex = state.items.findIndex((item) => item.id === companyData.id);
          if (existingIndex >= 0) {
            state.items[existingIndex] = companyData;
          } else {
            state.items.push(companyData);
          }
        }
      })
      .addCase(setupCompanyProfile.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      })
      .addCase(listCompanyDocuments.fulfilled, (state, action) => {
        state.documents = action.payload;
      })
      .addCase(uploadCompanyDocument.fulfilled, (state, action) => {
        state.documents.unshift(action.payload);
      })

      // Save for later
      .addCase(saveForLater.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(saveForLater.fulfilled, (state, action) => {
        state.isSaving = false;
        // Extract company_id from the backend response
        // Backend returns: { data: { company_id, user_id, is_active, ... } }
        const savedData = action.payload?.data || action.payload;
        const companyId =
          savedData?.company_id ||
          savedData?.companyId ||
          action.payload?.companyId ||
          action.payload?.id;

        if (companyId && !state.savedCompanyIds.includes(companyId)) {
          state.savedCompanyIds.push(companyId);
        }

        // Update saved companies list if payload contains full company data
        if (action.payload?.company || savedData?.company) {
          const companyData = action.payload?.company || savedData?.company;
          const existingIndex = state.savedCompanies.findIndex(
            (c) => c.id === companyId || c.company_id === companyId
          );
          if (existingIndex !== -1) {
            state.savedCompanies[existingIndex] = companyData;
          } else {
            state.savedCompanies.push(companyData);
          }
        }
      })
      .addCase(saveForLater.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload;
      })

      .addCase(removeFromSaved.pending, (state) => {
        state.isRemoving = true;
        state.saveError = null;
      })
      .addCase(removeFromSaved.fulfilled, (state, action) => {
        state.isRemoving = false;
        const companyId = action.payload;
        state.savedCompanyIds = state.savedCompanyIds.filter((id) => id !== companyId);
        state.savedCompanies = state.savedCompanies.filter(
          (c) => c.id !== companyId && c.company_id !== companyId
        );
      })
      .addCase(removeFromSaved.rejected, (state, action) => {
        state.isRemoving = false;
        state.saveError = action.payload;
      })

      .addCase(getSavedForLater.pending, (state) => {
        state.isLoadingSaved = true;
        state.saveError = null;
      })
      .addCase(getSavedForLater.fulfilled, (state, action) => {
        state.isLoadingSaved = false;
        // Backend returns: { data: [{ company_id, user_id, ... }, ...] }
        const responseData = action.payload?.data || action.payload;
        const saved = Array.isArray(responseData) ? responseData : [];
        state.savedCompanies = saved;
        // Extract company_id from each saved item
        state.savedCompanyIds = saved
          .map((item) => item.company_id || item.id || item.company?.id)
          .filter((id) => id !== undefined && id !== null);
      })
      .addCase(getSavedForLater.rejected, (state, action) => {
        state.isLoadingSaved = false;
        state.saveError = action.payload;
      })

      // Match scores
      .addCase(getMatchScore.pending, (state) => {
        state.isLoadingMatch = true;
        state.matchError = null;
      })
      .addCase(getMatchScore.fulfilled, (state, action) => {
        state.isLoadingMatch = false;
        const scores = Array.isArray(action.payload) ? action.payload : [];
        state.matchScores = scores;
      })
      .addCase(getMatchScore.rejected, (state, action) => {
        state.isLoadingMatch = false;
        state.matchError = action.payload;
      })

      .addCase(calculateMatchScore.pending, (state) => {
        state.isCalculatingMatch = true;
        state.matchError = null;
      })
      .addCase(calculateMatchScore.fulfilled, (state, action) => {
        state.isCalculatingMatch = false;
        const scores = Array.isArray(action.payload) ? action.payload : [];
        // Merge calculated scores with existing ones, updating if company_b_id matches
        scores.forEach((newScore) => {
          const existingIndex = state.matchScores.findIndex(
            (s) => s.company_b_id === newScore.company_b_id
          );
          if (existingIndex !== -1) {
            state.matchScores[existingIndex] = newScore;
          } else {
            state.matchScores.push(newScore);
          }
        });
      })
      .addCase(calculateMatchScore.rejected, (state, action) => {
        state.isCalculatingMatch = false;
        state.matchError = action.payload;
      })

      // Get DMS Company
      .addCase(getDMSCompany.pending, (state) => {
        state.isLoadingDMSCompany = true;
        state.dmsCompanyError = null;
      })
      .addCase(getDMSCompany.fulfilled, (state, action) => {
        state.isLoadingDMSCompany = false;
        state.dmsCompany = action.payload || null;
        // dmsCompany set in reducer
        state.dmsCompanyError = null;
      })
      .addCase(getDMSCompany.rejected, (state, action) => {
        state.isLoadingDMSCompany = false;
        state.dmsCompanyError = action.payload;
      })
      // Clear company data on logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.dmsCompany = null;
        state.myCompany = null;
        state.isLoadingDMSCompany = false;
        state.dmsCompanyError = null;
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  clearSaveError,
  clearMatchError,
  setCompanySavedStatus,
  updateOnboardingStep,
  setOnboardingStep,
  resetOnboarding,
  resetOnboardingStep,
  hydrateOnboardingFromCompany,
} = companySlice.actions;
export default companySlice.reducer;
